/**
 * File upload configuration
 */
export const FILE_UPLOAD_CONFIG = {
  /** Maximum file size in bytes (50MB) */
  MAX_FILE_SIZE: 50 * 1024 * 1024,
  /** Maximum number of files per upload */
  MAX_FILES: 10,
  /** Accepted file types for upload */
  ACCEPTED_TYPES: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  /** Maximum concurrent uploads */
  CONCURRENT_UPLOADS: 4,
  /** API base URL for file uploads */
  API_BASE_URL: 'https://scsbiz3.wearevisum.com',
  /** Upload endpoint path */
  UPLOAD_ENDPOINT: '/api/custom-line-item-images',
} as const;

/**
 * Progress and timing configuration
 */
export const TIMING_CONFIG = {
  /** Debounce delay for file input in milliseconds */
  FILE_INPUT_DEBOUNCE: 250,
  /** Throttle delay for progress updates in milliseconds */
  PROGRESS_THROTTLE: 100,
  /** Auto-close modal delay after successful upload */
  SUCCESS_AUTO_CLOSE_DELAY: 2000,
} as const;

/**
 * UI Configuration
 */
export const UI_CONFIG = {
  /** Number of decimal places for file size display */
  FILE_SIZE_DECIMALS: 2,
  /** Progress bar length for Unicode fallback */
  PROGRESS_BAR_LENGTH: 20,
  /** Progress bar filled character */
  PROGRESS_FILLED_CHAR: '█',
  /** Progress bar empty character */
  PROGRESS_EMPTY_CHAR: '░',
} as const;

/**
 * Logging configuration
 */
export const LOGGING_CONFIG = {
  /** Enable logging by default */
  ENABLED: true,
  /** Default log level */
  DEFAULT_LEVEL: 'debug' as const,
  /** Log prefix */
  PREFIX: '[Extension]',
} as const;

/**
 * Extension metadata
 */
export const EXTENSION_CONFIG = {
  /** Extension name */
  NAME: 'custom-order-photos',
  /** Current API version */
  API_VERSION: '2025-04',
  /** Extension namespace for metafields */
  NAMESPACE: 'custom_order',
  /** Metafield keys */
  METAFIELD_KEYS: {
    REQUIRES_PHOTOS: 'requires_photos',
    PHOTO_REQUIREMENTS: 'photo_requirements',
    UPLOADED_PHOTOS: 'uploaded_photos',
  },
} as const;

/**
 * Business logic constants
 */
export const BUSINESS_CONFIG = {
  /** Default photos per seat requirement */
  DEFAULT_PHOTOS_PER_SEAT: 1,
  /** Minimum photos required message threshold */
  MIN_PHOTOS_THRESHOLD: 1,
  /** Success message display duration */
  SUCCESS_MESSAGE_DURATION: 3000,
} as const;

/**
 * Error messages and fallbacks
 */
export const ERROR_MESSAGES = {
  GENERIC_UPLOAD_FAILED: 'Upload failed. Please try again.',
  FILE_TOO_LARGE: 'File too large. Maximum size is {maxSize}MB.',
  TOO_MANY_FILES: 'Too many files. Maximum is {maxFiles} files.',
  UNSUPPORTED_FILE_TYPE: 'File type not supported. Please use: {types}',
  NO_ORDER_ID: 'No order ID available for upload',
  ORDER_NOT_FOUND: 'Order not found',
  NETWORK_ERROR: 'Network error. Please check your connection.',
} as const;

/**
 * Translation keys for common UI elements
 */
export const TRANSLATION_KEYS = {
  MODAL: {
    TITLE: 'modal.title',
    CANCEL: 'modal.cancel',
    SUBMIT: 'modal.submit',
    UPLOADING: 'modal.uploading',
  },
  FILE_LIST: {
    TITLE: 'fileList.title',
    REMOVE: 'fileList.remove',
    UPLOADED: 'fileList.uploaded',
    ERROR: 'fileList.error',
    UPLOADING: 'fileList.uploading',
  },
  SUCCESS: {
    UPLOAD_COMPLETE: 'success.uploadComplete',
    UPLOAD_COMPLETE_PLURAL: 'success.uploadCompletePlural',
  },
  ERRORS: {
    FILES_REJECTED: 'errors.filesRejected',
    FILES_FAILED: 'errors.filesFailed',
    FILE_TYPE_NOT_SUPPORTED: 'errors.fileTypeNotSupported',
    FILE_TOO_LARGE: 'errors.fileTooLarge',
    TOO_MANY_FILES: 'errors.tooManyFiles',
  },
} as const;